<script setup lang="ts">
  // Menu-views stats page migrated to the V2 design system.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import StatsService from '@/api/services/stats.service';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsIntervalSelect from '@/components/v2/stats/V2StatsIntervalSelect.vue';
  import { TStatsChartInterval } from '@/components/v2/stats/v2-stats-interval.types';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import V2StatsSeriesReport from '@/components/v2/stats/V2StatsSeriesReport.vue';
  import { useStatsSeriesDisplay } from '@/composition/use-stats-series-display';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IStats } from '@/interfaces/IStats';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { buildChartLabels } from '@/statsutils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(() => restaurantStore.currency?.symbol ?? '');

  const range = ref<IStatsDateRange | null>(null);
  const interval = ref<TStatsChartInterval>('auto');

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  watch(interval, () => {
    load();
  });

  const statsData = ref<IStats | null>(null);
  const loading = ref(false);

  const SERIES_LABEL_KEYS: Record<string, string> = {
    links: 'v2Stats.series.menuLinks',
    tables: 'v2Stats.series.tableQr',
    otherQr: 'v2Stats.series.otherQr',
  };

  const SERIES_COLORS = ['#2c97f5', '#58b8f5', '#88caf5'];

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const stats = await statsService.getViewsStats(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        interval.value,
        undefined,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;

      stats.labels = buildChartLabels(stats);
      statsData.value = stats;
    } catch (error) {
      if (generation !== requestGeneration) return;
      statsData.value = null;
      ui.serverError(error, i18nT('v2Stats.views.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const displayStats = useStatsSeriesDisplay({
    stats: statsData,
    labelKeys: SERIES_LABEL_KEYS,
    colors: SERIES_COLORS,
    currencySymbol,
  });
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.views.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsIntervalSelect :value="interval" @input="interval = $event" />
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="views"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        storage-key="views"
        :text="String($t('statsNote.views'))"
      />
    </template>

    <V2StatsSeriesReport
      :stats="displayStats"
      :loading="loading"
      chart-type="stackedBar"
      :chart-title="String($t('v2Stats.views.chartTitle'))"
      :currency-symbol="currencySymbol"
      export-filename="views-stats"
      :export-sheet="String($t('v2Stats.views.title'))"
      :first-column-label="String($t('v2Stats.colDate'))"
    />
  </V2ReportPage>
</template>
